import { Store } from '@ngrx/store';
import { ChatChannelTypeEnum, ChatConversation } from 'lib-trend-core';
import { ChatShellService } from '../services/chat-shell.service';
import * as ChatActions from '../state/chat.actions';

/**
 * Opening a conversation from OUTSIDE the workspace (SPEC-2026-07-internal-chat,
 * T-22) — shared by the quick switcher (global overlay) and the search view.
 *
 * Both must behave exactly like the sidebar: mirror the choice into the store
 * (so every chat surface follows) AND hand the shell a target so the bubble
 * host surfaces it — the `/chat` route this used to navigate to is gone.
 * Kept as plain functions so neither component depends on the other.
 */

/** Opens an existing conversation in the chat shell (popup or workspace). */
export function openChatConversation(store: Store, shell: ChatShellService, channelId: string, messageId?: string): void {
  store.dispatch(ChatActions.openConversation({ channelId }));
  // `messageId` is the jump target: the conversation pane reveals + highlights
  // it, seeking older history pages when needed.
  shell.open({ channelId, messageId });
}

/**
 * Opens the DIRECT conversation with a person: an existing DM (matched by
 * `directKey`) opens directly, otherwise a DRAFT opens — DMs only materialize
 * server-side on the first MESSAGE_SEND `{ directUserId }` (RF-01). Selecting
 * yourself opens the personal-notes self pair.
 */
export function openChatDirect(
  store: Store,
  shell: ChatShellService,
  currentUserId: string,
  userId: string,
  conversations: ChatConversation[],
): void {
  const directKey = [currentUserId, userId].sort().join(':');
  const existing = (conversations ?? []).find((conversation) =>
    conversation.type === ChatChannelTypeEnum.DIRECT && conversation.directKey === directKey);

  if (existing) {
    openChatConversation(store, shell, existing._id);
    return;
  }

  store.dispatch(ChatActions.openDirectDraft({ userId, directKey }));
  shell.open({ directUserId: userId });
}
